import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { History, Play, MessageSquare, Sparkles } from "lucide-react";
import { useLang } from "@/context/AppContext";
import { useLiveEvent } from "@/context/RealtimeContext";
import { ReplayPlayer, fmtT } from "@/components/ReplayPlayer";
import { useApi } from "@/lib/useApi";


export const Replays = ({ cid, joined }) => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sharedId = params.get("replay");
  const sharedT = Number(params.get("t")) || 0;
  const [open, setOpen] = useState(sharedId);
  const panel = useRef(null);
  useEffect(() => { if (sharedId && panel.current) panel.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [sharedId]);
  const { data: items, reload: load } = useApi(`/communities/${cid}/replays`, { initial: [] });
  useLiveEvent(["community_update"], (m) => m.data.community_id === cid && load());
  return (
    <section ref={panel} className="scroll-mt-24 rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="replays-panel">
      <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-cyan-300"><History className="h-4 w-4" />{t("replays")}</p>
      {sharedId && !joined && <p className="mb-3 rounded border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-300" data-testid="replay-join-required">{t("join_to_watch")}</p>}
      {!items.length && <p className="text-sm text-slate-500">{t("no_replays")}</p>}
      <div className="space-y-2">
        {items.map((b) => (
          <div key={b.id}>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-white/5 px-3 py-2" data-testid={`replay-item-${b.id}`}>
              <div>
                <p className="text-sm text-white">{b.title}</p>
                <p className="font-mono text-[10px] text-slate-500">{t("host")}: {b.host_name} · {new Date(b.started_at).toLocaleString()} · {fmtT(b.duration_s)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-400"><MessageSquare className="h-3.5 w-3.5" />{b.comments}</span>
                <span className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-400"><Sparkles className="h-3.5 w-3.5" />{b.reactions}</span>
                <button disabled={!joined} onClick={() => setOpen(open === b.id ? null : b.id)} data-testid={`replay-open-${b.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-cyan-400/40 px-3 py-1 text-xs text-cyan-300 transition-colors hover:bg-cyan-400 hover:text-slate-950 disabled:opacity-40">
                  <Play className="h-3.5 w-3.5" />{open === b.id ? t("close_live") : t("replay")}
                </button>
              </div>
            </div>
            {open === b.id && joined && <ReplayPlayer bid={b.id} cid={cid} start={b.id === sharedId ? Math.min(sharedT, b.duration_s) : 0} />}
          </div>
        ))}
      </div>
    </section>
  );
};
